import { brandLanding } from '@/constants/routes';

import { Box1, Location, MedalStar, ShieldTick, Shop, TickCircle, Timer1, TruckFast, Verify, WalletMoney } from 'iconsax-react';

export const PRODUCTS_SECTION_ID = 'brand-products';
export const ABOUT_SECTION_ID = 'brand-about';
export const PRODUCTS_PAGE_SIZE = 12;

export type BrandShippingScope = 'nationwide' | 'batteryCities';

export const BATTERY_COVERAGE_CITY_NAMES = ['تهران', 'مشهد', 'اصفهان', 'رشت', 'کرج'] as const;
export const BATTERY_COVERAGE_CITY_COUNT = BATTERY_COVERAGE_CITY_NAMES.length;
export const NATIONWIDE_CITY_COUNT = 31;

export const heroCopy = {
    primaryCta: 'مشاهده محصولات',
    secondaryCta: 'مشاوره خرید'
};

export const heroTrustPanelCopy = {
    badge: 'خرید مطمئن'
};

export const landingStatsLabels = {
    products: 'محصول فعال',
    cities: 'شهر تحت پوشش',
    satisfaction: 'رضایت خریداران'
};

export const landingStatsDefaults = {
    satisfaction: 98
};

export const howItWorksCopy = {
    badge: 'مسیر ساده',
    title: 'خرید از این برند در ۳ قدم',
    subtitle: 'از انتخاب محصول تا تحویل، همه چیز شفاف و قابل پیگیری است.'
};

export const aboutCopy = {
    badge: 'درباره برند',
    titleSuffix: 'را بهتر بشناس',
    emptySubtitle: 'اطلاعات تکمیلی این برند به‌زودی تکمیل می‌شود.'
};

export const faqCopy = {
    badge: 'سوالات متداول',
    titlePrefix: 'سوالات متداول خرید محصولات'
};

export const productsCopy = {
    badge: 'کاتالوگ برند',
    title: 'محصولات این برند',
    subtitle: 'قیمت، موجودی و مشخصات را مقایسه کن و همان‌جا سفارش بده.',
    allCategories: 'همه محصولات',
    loadMore: 'مشاهده محصولات بیشتر',
    emptyTitle: 'محصولی برای این فیلتر پیدا نشد',
    emptySubtitle: 'فیلتر دیگری را امتحان کن یا با پشتیبانی تماس بگیر.',
    errorTitle: 'دریافت محصولات با مشکل مواجه شد'
};

export const finalCtaCopy = {
    titlePrefix: 'محصولات اصل',
    titleSuffix: 'را همین حالا سفارش بده',
    cta: 'مشاهده محصولات برند'
};

export const stickyCtaCopy = {
    titleSuffix: 'اصل',
    cta: 'مشاهده محصولات'
};

const nationwideShipping = {
    trustPoint: 'ارسال به سراسر ایران',
    trustPanelItem: 'ارسال سراسری',
    trustStripTitle: 'ارسال سراسری',
    trustStripDescription: 'سفارش را به شهرهای مختلف ایران با زمان‌بندی شفاف ارسال می‌کنیم.',
    citiesStat: NATIONWIDE_CITY_COUNT,
    citiesStatSuffix: '+',
    deliveryStepDescription: 'سفارش به شهرت ارسال می‌شود؛ زمان تحویل هنگام ثبت سفارش مشخص است.',
    benefitTitle: 'ارسال به همه جا',
    benefitDescription: 'تحویل در شهرهای مختلف ایران با پیگیری وضعیت سفارش.',
    benefitsSubtitle: 'اصالت کالا، قیمت شفاف و ارسال به سراسر ایران.',
    finalCtaSubtitle: 'از کاتالوگ برند انتخاب کن؛ ارسال به سراسر ایران و پشتیبانی تخصصی خرید.',
    finalCtaFootnote: 'کالای اصل • قیمت شفاف • ارسال سراسری',
    finalCtaBadge: 'کالای اصل • ارسال به سراسر ایران',
    stickySubtitle: 'ارسال به سراسر ایران',
    defaultDescription: (brandName: string) => `محصولات اصل ${brandName} را با قیمت شفاف، موجودی به‌روز و ارسال به سراسر ایران از ازکی‌سرویس بخر.`,
    metaDescription: (brandName: string) => `خرید آنلاین محصولات اصل ${brandName} با قیمت شفاف، موجودی به‌روز و ارسال به سراسر ایران از ازکی‌سرویس.`,
    faqShippingAnswer: 'ارسال به سراسر ایران فعال است. زمان دقیق تحویل پس از ثبت آدرس و بر اساس شهر مقصد مشخص می‌شود.',
    faqWhyAnswer: (brandName: string) =>
        `قیمت شفاف، موجودی به‌روز، ارسال سراسری و پشتیبانی تخصصی خرید باعث می‌شود سفارش ${brandName} سریع‌تر و مطمئن‌تر انجام شود.`
};

const batteryCitiesShipping = {
    trustPoint: 'ارسال و نصب در برخی از شهرها',
    trustPanelItem: 'ارسال و نصب در برخی از شهرها',
    trustStripTitle: 'ارسال و نصب در برخی از شهرها',
    trustStripDescription: 'ارسال و نصب باتری در حال حاضر در برخی از شهرها فعال است.',
    citiesStat: BATTERY_COVERAGE_CITY_COUNT,
    citiesStatSuffix: '',
    deliveryStepDescription: 'سفارش در برخی از شهرها قابل ثبت، ارسال و نصب است؛ پوشش و زمان تحویل هنگام ثبت سفارش مشخص می‌شود.',
    benefitTitle: 'ارسال و نصب در برخی از شهرها',
    benefitDescription: 'ارسال و نصب باتری در حال حاضر در برخی از شهرها انجام می‌شود.',
    benefitsSubtitle: 'اصالت کالا، قیمت شفاف و ارسال و نصب در برخی از شهرها.',
    finalCtaSubtitle: 'از کاتالوگ برند انتخاب کن؛ ارسال و نصب در برخی از شهرها و پشتیبانی تخصصی خرید.',
    finalCtaFootnote: 'کالای اصل • قیمت شفاف • ارسال و نصب در برخی از شهرها',
    finalCtaBadge: 'کالای اصل • ارسال و نصب در برخی از شهرها',
    stickySubtitle: 'ارسال و نصب در برخی از شهرها',
    defaultDescription: (brandName: string) =>
        `محصولات اصل ${brandName} را با قیمت شفاف، موجودی به‌روز و ارسال و نصب در برخی از شهرها از ازکی‌سرویس بخر.`,
    metaDescription: (brandName: string) =>
        `خرید آنلاین محصولات اصل ${brandName} با قیمت شفاف، موجودی به‌روز و ارسال و نصب در برخی از شهرها از ازکی‌سرویس.`,
    faqShippingAnswer: 'ارسال و نصب محصولات باتری این برند در حال حاضر در برخی از شهرها فعال است. پوشش و زمان دقیق تحویل پس از ثبت آدرس مشخص می‌شود.',
    faqWhyAnswer: (brandName: string) =>
        `قیمت شفاف، موجودی به‌روز، ارسال و نصب در برخی از شهرها و پشتیبانی تخصصی خرید باعث می‌شود سفارش ${brandName} سریع‌تر و مطمئن‌تر انجام شود.`
};

export const getBrandShippingContent = (scope: BrandShippingScope) => (scope === 'batteryCities' ? batteryCitiesShipping : nationwideShipping);

export const getBrandTrustPoints = (scope: BrandShippingScope) => {
    const shipping = getBrandShippingContent(scope);
    return [
        { icon: ShieldTick, text: 'ضمانت اصالت کالا' },
        { icon: TruckFast, text: shipping.trustPoint },
        { icon: Verify, text: 'پشتیبانی تخصصی خرید' }
    ];
};

export const getBrandTrustPanelItems = (scope: BrandShippingScope) => {
    const shipping = getBrandShippingContent(scope);
    return [
        { icon: ShieldTick, text: 'ضمانت اصالت کالا' },
        { icon: TruckFast, text: shipping.trustPanelItem },
        { icon: Verify, text: 'پشتیبانی تخصصی' }
    ];
};

export const getBrandTrustStripItems = (scope: BrandShippingScope) => {
    const shipping = getBrandShippingContent(scope);
    return [
        {
            icon: ShieldTick,
            title: 'اصالت کالا',
            description: 'محصولات برند از تأمین‌کنندگان معتبر با ضمانت اصالت عرضه می‌شوند.'
        },
        {
            icon: scope === 'batteryCities' ? Location : TruckFast,
            title: shipping.trustStripTitle,
            description: shipping.trustStripDescription
        },
        {
            icon: WalletMoney,
            title: 'پرداخت امن',
            description: 'قیمت نهایی قبل از پرداخت مشخص است؛ امکان خرید اقساطی هم فراهم است.'
        },
        {
            icon: Timer1,
            title: 'پشتیبانی واقعی',
            description: 'قبل و بعد از خرید، برای انتخاب محصول مناسب راهنمایی می‌گیری.'
        }
    ];
};

export const getBrandHowItWorksSteps = (scope: BrandShippingScope) => {
    const shipping = getBrandShippingContent(scope);
    return [
        {
            icon: Shop,
            title: 'محصول را انتخاب کن',
            description: 'از کاتالوگ برند، محصول مناسب خودرو یا موتورسیکلتت را پیدا کن.'
        },
        {
            icon: TickCircle,
            title: 'سفارش را ثبت کن',
            description: 'موجودی، قیمت و مشخصات را ببین و با چند کلیک خرید را نهایی کن.'
        },
        {
            icon: TruckFast,
            title: 'تحویل بگیر',
            description: shipping.deliveryStepDescription
        }
    ];
};

export const getBrandBenefits = (scope: BrandShippingScope) => {
    const shipping = getBrandShippingContent(scope);
    return [
        {
            icon: MedalStar,
            title: 'برند معتبر بازار',
            description: 'دسترسی مستقیم به محصولات همین برند بدون واسطه و سردرگمی.'
        },
        {
            icon: Box1,
            title: 'تنوع واقعی محصولات',
            description: 'باتری، روغن و لوازم مصرفی مرتبط با برند در یک صفحه برای مقایسه آسان.'
        },
        {
            icon: ShieldTick,
            title: 'ضمانت اصالت',
            description: 'کالای اصل با گارانتی برند و پشتیبانی ازکی‌سرویس.'
        },
        {
            icon: TruckFast,
            title: shipping.benefitTitle,
            description: shipping.benefitDescription
        },
        {
            icon: Verify,
            title: 'قیمت شفاف',
            description: 'تخفیف‌ها و قیمت نهایی قبل از پرداخت به‌صورت واضح نمایش داده می‌شود.'
        },
        {
            icon: Timer1,
            title: 'راهنمایی تخصصی',
            description: 'اگر بین مدل‌ها مردد هستی، پشتیبانی کمک می‌کند انتخاب درست داشته باشی.'
        }
    ];
};

export const brandCanonicalBase = `https://azkiservice.com${brandLanding}`;
